import { useAuth } from '@context/AuthContext';
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import {
  GetGiftCampaignsByIdsQuery,
  useGetGiftCampaignsByIdsLazyQuery,
  GetGiftCampaignsByIdsLazyQueryHookResult,
} from '.';

export type Campaigns = GetGiftCampaignsByIdsQuery['campaigns'];

interface CampaignsContextType {
  campaigns?: Campaigns;
  campaignsLoading?: boolean;
  setCampaignIds?: React.Dispatch<React.SetStateAction<string[]>>;
  refetch?: GetGiftCampaignsByIdsLazyQueryHookResult[1]['refetch'];
}

interface CampaignsControllerProps {
  children?: React.ReactNode;
}

const mergeCampaigns = (existingCampaigns: Campaigns, incomingCampaigns: Campaigns) => {
  const newMoreCampaignIds = incomingCampaigns.map(campaign => campaign.id);
  const newState = existingCampaigns
    .filter(campaign => !newMoreCampaignIds.includes(campaign.id))
    .concat(incomingCampaigns);
  return newState;
};

const CampaignsContext = createContext<CampaignsContextType>({});

export const CampaignsController = ({ children }: CampaignsControllerProps) => {
  const { isLoggedIn } = useAuth();

  const [campaigns, setCampaigns] = useState<Campaigns>([]);
  const [campaignIds, setCampaignIds] = useState<string[]>([]);

  const [fetchGiftCampaignByIds, { loading, data, error, refetch }] = useGetGiftCampaignsByIdsLazyQuery({
    variables: {
      ids: campaignIds,
    },
    fetchPolicy: 'no-cache',
  });
  const newMoreCampaigns = data?.campaigns;

  useEffect(() => {
    if (isLoggedIn) {
      fetchGiftCampaignByIds();
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!newMoreCampaigns) return;
    setCampaigns(prevState => mergeCampaigns(prevState, newMoreCampaigns));
  }, [newMoreCampaigns]);

  const context = useMemo(() => {
    const loadingContainer = loading && !data && !error;

    return {
      campaigns: campaigns,
      campaignsLoading: loadingContainer,
      setCampaignIds,
      refetch,
    };
  }, [campaigns, loading, error]);

  return <CampaignsContext.Provider value={context}>{children}</CampaignsContext.Provider>;
};

export const useCampaigns = () => {
  const context = useContext(CampaignsContext);
  if (!context) {
    throw new Error('useCampaigns must be used within CampaignsController');
  }
  return context;
};
